import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePushNotifications } from './usePushNotifications';

/**
 * The push enable/disable control (PLAN slice 13b, item 2), per-DEVICE. Lives in
 * the app header so it is reachable on EVERY viewport — the target user is a fleet
 * manager on an Android phone, and the desktop-only Alerts screen would hide this
 * from exactly them.
 *
 * ABSENCE OVER DISABLED (CLAUDE.md, AC-12). This renders NOTHING while the VAPID
 * key query is loading and NOTHING when the feature is unconfigured or the browser
 * cannot do push — never a greyed-out or "coming soon" control. The VAPID private
 * key lands in `.env` by Mahad's hand AFTER merge, so `unavailable` is the real
 * state on day one and the whole control must be absent then.
 *
 * iOS REALITY (AC-13). iOS delivers web push only to a Home-Screen install. In a
 * browser tab we STATE that installation is required and offer no enable button
 * that the platform could not honour.
 */
export function PushToggle() {
  const { t } = useTranslation();
  const { availability, subscribed, permission, busy, error, enable, disable } =
    usePushNotifications();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close the panel on an outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Absent, not disabled: loading (no flash), unconfigured, or unsupported.
  if (availability === 'loading' || availability === 'unavailable') return null;

  const active = availability === 'ready' && subscribed;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={t('push.control.label')}
        className="relative flex size-11 items-center justify-center rounded-xl border border-raad-border bg-raad-panel text-raad-text transition-colors hover:border-raad-line-20"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9 M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {active ? (
          <span
            aria-hidden="true"
            className="absolute right-2.5 top-2.5 size-2 rounded-full bg-raad-accent ring-2 ring-raad-panel"
          />
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={t('push.heading')}
          className="absolute right-0 z-20 mt-2 w-72 rounded-panel border border-raad-border bg-raad-panel p-4 shadow-xl"
        >
          <h2 className="font-display text-sm font-bold text-raad-text">{t('push.heading')}</h2>

          {availability === 'needs-install' ? (
            <div className="mt-2">
              <p className="text-[12.5px] font-semibold text-raad-text">{t('push.iosInstall.title')}</p>
              <p className="mt-1.5 text-[12px] leading-relaxed text-raad-muted">{t('push.iosInstall.body')}</p>
            </div>
          ) : (
            <>
              <p className="mt-1.5 text-[12px] leading-relaxed text-raad-muted">{t('push.description')}</p>

              <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-raad-dim">
                {subscribed ? t('push.status.on') : t('push.status.off')}
              </p>

              {permission === 'denied' && !subscribed ? (
                <p className="mt-2 text-[12px] leading-relaxed text-raad-warn">{t('push.denied')}</p>
              ) : subscribed ? (
                <button
                  type="button"
                  onClick={() => void disable()}
                  disabled={busy}
                  className="mt-3 min-h-11 w-full rounded-xl border border-raad-border px-4 text-sm font-semibold text-raad-text transition-colors hover:border-raad-line-20 disabled:opacity-60"
                >
                  {busy ? t('push.disabling') : t('push.disable')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void enable()}
                  disabled={busy}
                  className="mt-3 min-h-11 w-full rounded-xl bg-raad-primary px-4 text-sm font-semibold text-raad-onprimary transition-colors hover:bg-raad-accent disabled:opacity-60"
                >
                  {busy ? t('push.enabling') : t('push.enable')}
                </button>
              )}

              {error === 'failed' ? (
                <p role="alert" className="mt-2 text-[12px] leading-relaxed text-raad-warn">{t('push.error')}</p>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default PushToggle;
